<template>
<!-- ========== Left Sidebar Start ========== -->

<!--- Sidemenu -->
<div id="sidebar-menu">
    <!-- Left Menu Start -->
    <ul id="side-menu" class="metismenu list-unstyled">
        <template v-for="item in menuItems">
            <li class="menu-title" v-if="item.isTitle && hasPerm(item)" :key="item.id">
                {{ $t(item.label) }}
            </li>
            <li v-if="!item.isTitle && !item.isLayout" :key="item.id">
                <a v-if="hasItems(item) && hasPerm(item)" href="javascript:void(0);" class="is-parent" :class="{ 'has-arrow': !item.badge, 'has-dropdown': item.badge }">
                    <i :class="`${item.icon}`" v-if="item.icon"></i>
                    <span>{{ $t(item.label) }}</span>
                    <span :class="`badge badge-pill badge-${item.badge.variant} float-right`" v-if="item.badge">{{ $t(item.badge.text) }}</span>
                </a>

                <router-link :to="item.link" v-if="!hasItems(item) && hasPerm(item)" class="side-nav-link-ref">
                    <i :class="`${item.icon}`" v-if="item.icon"></i>
                    <span>{{ $t(item.label) }}</span>
                    <span :class="`badge badge-pill badge-${item.badge.variant} float-right`" v-if="item.badge">{{ $t(item.badge.text) }}</span>
                    <span v-if="item.requiresWaMessenger && waPendingCount > 0" class="wa-menu-badge">{{ waPendingCount }}</span>
                </router-link>

                <ul v-if="hasItems(item)" class="sub-menu" aria-expanded="false">
                    <template v-for="(subitem, index) of item.subItems">
                        <li v-if="hasPerm(subitem)" :key="index">
                            <router-link :to="subitem.link" v-if="!hasItems(subitem)" class="side-nav-link-ref">{{ $t(subitem.label) }}</router-link>
                            <a v-if="hasItems(subitem)" class="side-nav-link-a-ref has-arrow" href="javascript:void(0);">{{ $t(subitem.label) }}</a>
                            <ul v-if="hasItems(subitem)" class="sub-menu mm-collapse" aria-expanded="false">
                                <template v-for="(subSubitem, subIndex) of subitem.subItems">
                                    <li v-if="hasPerm(subSubitem)" :key="subIndex">
                                        <router-link :to="subSubitem.link" class="side-nav-link-ref">{{ $t(subSubitem.label) }}</router-link>
                                    </li>
                                </template>
                            </ul>
                        </li>
                    </template>
                </ul>
            </li>
        </template>
    </ul>
</div>
<!-- Sidebar -->
</template>
<script>
import MetisMenu from 'metismenujs/dist/metismenujs'
import { menuItems } from './menu'
import { mapState, mapGetters } from 'vuex'

export default {
  data () {
    return {
      menuItems: menuItems,
      menuData: null,
      waPendingCount: 0,
      waChannel: null
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('settingRSVP', ['settingRSVP']),
    ...mapState('wa', ['settingWA']),
    ...mapState('app', { appConfig: 'config' }),
    ...mapGetters('auth', ['hasPermission', 'isSuperAdmin'])
  },
  watch: {
    $route () {
      this.$nextTick(() => {
        this.highlightActiveMenu()
        // Duas passadas: uma logo após o DOM assentar e outra depois das
        // animações de collapse do MetisMenu (~350ms), que mudam a altura
        // do menu e podem empurrar o item ativo para fora da vista.
        setTimeout(() => this.ensureActiveVisible('nearest'), 60)
        setTimeout(() => this.ensureActiveVisible('nearest'), 450)
      })
    }
  },
  async mounted () {
    // Garante que as configurações RSVP estejam carregadas
    if (!this.settingRSVP || Object.keys(this.settingRSVP).length === 0) {
      try {
        await this.$store.dispatch('settingRSVP/GetRSVP')
      } catch (err) {
        console.warn('Failed to load RSVP settings in side-nav:', err)
      }
    }

    // Carregar settings WA para controlar visibilidade do menu Messenger
    if (!this.settingWA || Object.keys(this.settingWA).length === 0) {
      try {
        await this.$store.dispatch('wa/GetSettingWA')
      } catch (err) {
        console.warn('Failed to load WA settings in side-nav:', err)
      }
    }

    // Carregar config do App para controlar visibilidade do módulo "App"
    if (this.hasPermission('app.manage') || this.isSuperAdmin) {
      try {
        await this.$store.dispatch('app/GetAppConfig')
      } catch (err) {
        console.warn('Failed to load App config in side-nav:', err)
      }
    }

    // eslint-disable-next-line no-unused-vars
    var menuRef = new MetisMenu('#side-menu')
    this.highlightActiveMenu(true)

    // Setup Pusher para badge de conversas pendentes no menu Messenger
    this.setupWaBadge()

    // Ler pendentes do localStorage
    var savedWa = localStorage.getItem('wa_pending_notifications')
    if (savedWa) {
      try {
        var parsed = JSON.parse(savedWa)
        this.waPendingCount = Array.isArray(parsed) ? parsed.length : 0
      } catch (e) {}
    }

    // Observar mudancas no localStorage
    window.addEventListener('storage', this.onStorageChange)
    // Polling para mesma aba (storage event so dispara entre abas)
    this.waStorageInterval = setInterval(this.syncWaBadgeFromStorage, 2000)
  },
  beforeDestroy () {
    if (this.waChannel) {
      this.waChannel.unbind_all()
      this.$pusher.unsubscribe(this.waChannel.name)
    }
    window.removeEventListener('storage', this.onStorageChange)
    if (this.waStorageInterval) clearInterval(this.waStorageInterval)
  },
  methods: {
    highlightActiveMenu (scrollToItem = false) {
      // Recalcula o destaque do menu. Com o layout persistente (AppShell)
      // o side-nav nao re-monta a cada navegacao - sem isto, o item ativo
      // ficava congelado na tela anterior.
      document.querySelectorAll('#side-menu .active, #side-menu .mm-active').forEach(el => {
        el.classList.remove('active')
        el.classList.remove('mm-active')
      })

      var links = document.getElementsByClassName('side-nav-link-ref')
      var matchingMenuItem = null
      const paths = []

      for (var i = 0; i < links.length; i++) {
        paths.push(links[i]['pathname'])
      }
      var itemIndex = paths.indexOf(window.location.pathname)
      if (itemIndex === -1) {
        const strIndex = window.location.pathname.lastIndexOf('/')
        const item = window.location.pathname.substr(0, strIndex).toString()
        matchingMenuItem = links[paths.indexOf(item)]
      } else {
        matchingMenuItem = links[itemIndex]
      }

      if (matchingMenuItem) {
        matchingMenuItem.classList.add('active')
        var parent = matchingMenuItem.parentElement

        /**
         * TODO: This is hard coded way of expading/activating parent menu dropdown and working till level 3.
         * We should come up with non hard coded approach
         */
        if (parent) {
          parent.classList.add('mm-active')
          const parent2 = parent.parentElement.closest('ul')
          if (parent2 && parent2.id !== 'side-menu') {
            parent2.classList.add('mm-show')

            const parent3 = parent2.parentElement
            if (parent3) {
              parent3.classList.add('mm-active')
              var childAnchor = parent3.querySelector('.has-arrow')
              var childDropdown = parent3.querySelector('.has-dropdown')
              if (childAnchor) childAnchor.classList.add('mm-active')
              if (childDropdown) childDropdown.classList.add('mm-active')

              const parent4 = parent3.parentElement
              if (parent4 && parent4.id !== 'side-menu') {
                parent4.classList.add('mm-show')
                const parent5 = parent4.parentElement
                if (parent5 && parent5.id !== 'side-menu') {
                  parent5.classList.add('mm-active')
                  const childanchor = parent5.querySelector('.is-parent')
                  if (childanchor && parent5.id !== 'side-menu') {
                    childanchor.classList.add('mm-active')
                  }
                }
              }
            }
          }
        }
      }

      // Na montagem do menu (entrada vinda de outro módulo, scroll nasce no
      // topo) centraliza o item ativo para dar contexto.
      if (matchingMenuItem && scrollToItem) {
        setTimeout(() => this.ensureActiveVisible('center'), 150)
      }
    },
    /**
     * Garante que o item ativo esteja visível na sidebar.
     * - 'center': centraliza (usado na montagem do menu).
     * - 'nearest': movimento mínimo e suave, e só se estiver fora da área
     *   visível (usado após navegar — animações de collapse podem deslocar
     *   o conteúdo e empurrar o item para fora da vista).
     */
    ensureActiveVisible (mode) {
      const active = document.querySelector('#side-menu .side-nav-link-ref.active')
      if (!active) return
      const scroller = active.closest('.simplebar-content-wrapper')
      if (!scroller) return
      const itemRect = active.getBoundingClientRect()
      const scrollRect = scroller.getBoundingClientRect()
      const outOfView = itemRect.top < scrollRect.top || itemRect.bottom > scrollRect.bottom
      if (!outOfView) return
      if (mode === 'center') {
        scroller.scrollTop += itemRect.top - scrollRect.top - (scrollRect.height / 2) + (itemRect.height / 2)
      } else {
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    },
    setupWaBadge () {
      if (!this.settingWA || parseInt(this.settingWA.whatsapp) !== 1 || !this.settingWA.wa_messenger) return
      var slug = process.env.VUE_APP_EVENT_NAME || ''
      var channel = slug ? 'wa-messenger-' + slug : 'wa-messenger'
      this.waChannel = this.$pusher.subscribe(channel)
      this.waChannel.bind('new-message', this.onWaMessageForBadge)
    },
    onWaMessageForBadge (data) {
      var conv = data.conversation || {}
      var msg = data.message || {}
      var isInbound = msg.sender === 'contact' || msg.direction === 'inbound'
      var needsHuman = !conv.assigned_admin_id && conv.status !== 'bot'
      var isHandoff = msg.message_type === 'system' && (msg.content || '').toLowerCase().includes('transferiu')
      if (!isInbound && !isHandoff) return
      if (!needsHuman && !isHandoff) return

      // Atualizar contador lendo do localStorage (fonte unica de verdade)
      var saved = localStorage.getItem('wa_pending_notifications')
      var list = []
      if (saved) {
        try { list = JSON.parse(saved) } catch (e) {}
      }
      this.waPendingCount = list.length || (this.waPendingCount + 1)
    },
    onStorageChange (event) {
      if (event.key === 'wa_pending_notifications') {
        this.syncWaBadgeFromStorage()
      }
    },
    syncWaBadgeFromStorage () {
      var saved = localStorage.getItem('wa_pending_notifications')
      if (!saved) {
        this.waPendingCount = 0
        return
      }
      try {
        var list = JSON.parse(saved)
        this.waPendingCount = Array.isArray(list) ? list.length : 0
      } catch (e) {
        this.waPendingCount = 0
      }
    },
    /**
     * Returns true or false if given menu item has child or not
     * @param item menuItem
     */
    hasItems (item) {
      return item.subItems !== undefined ? item.subItems.length > 0 : false
    },
    toggleMenu (event) {
      event.currentTarget.nextElementSibling.classList.toggle('mm-show')
    },
    hasPerm (item) {
      // Verifica se o menu requer pagamento habilitado
      if (item.requiresPayment) {
        const hasPayment = this.settingRSVP?.has_payment === true ||
                          this.settingRSVP?.has_payment === 1 ||
                          this.settingRSVP?.has_payment === '1'

        if (!hasPayment) {
          return false
        }
      }

      // Verifica se o menu requer WhatsApp Messenger habilitado
      if (item.requiresWaMessenger) {
        var waActive = this.settingWA &&
          (parseInt(this.settingWA.whatsapp) === 1) &&
          (this.settingWA.wa_messenger === true || this.settingWA.wa_messenger === 1 || this.settingWA.wa_messenger === '1')
        if (!waActive) {
          return false
        }
      }

      // Verifica se o menu requer controle de inscrição habilitado
      if (item.requiresRegistrationControl) {
        const rcEnabled = this.settingRSVP?.registration_control_enabled === true ||
                          this.settingRSVP?.registration_control_enabled === 1 ||
                          this.settingRSVP?.registration_control_enabled === '1'

        if (!rcEnabled) {
          return false
        }
      }

      // Verifica se o menu requer logística (aéreo) habilitado
      if (item.requiresLogistics) {
        const hasLogistics = this.settingRSVP?.has_logistics === true ||
                             this.settingRSVP?.has_logistics === 1 ||
                             this.settingRSVP?.has_logistics === '1'

        if (!hasLogistics) {
          return false
        }
      }

      // Verifica se o menu requer o App habilitado para este evento
      if (item.requiresApp) {
        const appEnabled = this.appConfig?.app_enabled === true ||
                           this.appConfig?.app_enabled === 1 ||
                           this.appConfig?.app_enabled === '1'

        if (!appEnabled) {
          return false
        }
      }

      // Se não tem permissão definida, mostra sempre
      if (!item.perm || item.perm === null) {
        return true
      }

      // Super admin tem acesso a tudo
      if (this.isSuperAdmin) {
        return true
      }

      // Verifica se tem a permissão específica
      return this.hasPermission(item.perm)
    }
  }
}
</script>

<style>
.wa-menu-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #25d366;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  margin-left: auto;
  float: right;
  margin-top: 2px;
}
</style>
